import { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Filter,
  Calendar,
  User,
  Loader2,
  MoreVertical,
  Trash2,
  Edit,
  Check,
} from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useDebounce } from '../../hooks/useDebounce';
import { PageHeader, SearchInput } from '../../components/ui';
import { formatDate, formatDateTime } from '../../utils/format';
import { StatCard, Panel, Badge, Button, buttonClass } from '../../design-system';

// ==================== INTERFACES ====================

interface Tarefa {
  id: number;
  titulo: string;
  descricao: string | null;
  tipo: string | null;
  prioridade: string;
  dataVencimento: string;
  concluida: boolean;
  dataConclusao: string | null;
  dataCadastro: string;
  usuarioId: number | null;
  usuarioNome: string | null;
  leadId: number | null;
  leadNome: string | null;
  veiculoId: number | null;
  veiculoDescricao: string | null;
}

interface Contadores {
  pendentes: number;
  atrasadas: number;
  paraHoje: number;
  concluidasHoje: number;
}

interface Vendedor {
  id: number;
  nome: string;
}

interface TarefasResponse {
  items: Tarefa[];
  totalItems: number;
  page: number;
  pageSize: number;
  contadores: Contadores;
  vendedores: Vendedor[];
}

// ==================== CONSTANTES ====================

const prioridadeConfig: Record<string, { label: string; tone: 'danger' | 'warning' | 'success' | 'neutral' }> = {
  URGENTE: { label: 'Urgente', tone: 'danger' },
  ALTA: { label: 'Alta', tone: 'warning' },
  MEDIA: { label: 'Média', tone: 'warning' },
  BAIXA: { label: 'Baixa', tone: 'success' },
};

const tipoConfig: Record<string, { label: string; icon: string }> = {
  LIGACAO: { label: 'Ligação', icon: '📞' },
  EMAIL: { label: 'E-mail', icon: '📧' },
  REUNIAO: { label: 'Reunião', icon: '👥' },
  VISITA: { label: 'Visita', icon: '🏠' },
  TEST_DRIVE: { label: 'Test Drive', icon: '🚗' },
  FOLLOW_UP: { label: 'Follow-up', icon: '🔔' },
  OUTRO: { label: 'Outro', icon: '📋' },
};

// ==================== COMPONENTES MEMORIZADOS ====================

interface TarefaItemProps {
  tarefa: Tarefa;
  isAtrasada: boolean;
  isHoje: boolean;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onConcluir: () => void;
  onDelete: () => void;
  onNavigate: (path: string) => void;
}

const TarefaItem = memo(function TarefaItem({
  tarefa,
  isAtrasada,
  isHoje,
  menuOpen,
  onToggleMenu,
  onConcluir,
  onDelete,
  onNavigate,
}: TarefaItemProps) {
  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all hover:border-slate-300 hover:shadow-[0_10px_30px_-12px_rgba(15,23,42,0.18)] ${
        tarefa.concluida
          ? 'border-slate-200/70 opacity-60'
          : isAtrasada
            ? 'border-rose-200 bg-rose-50/40'
            : 'border-slate-200/70'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Checkbox */}
        <button
          onClick={!tarefa.concluida ? onConcluir : undefined}
          disabled={tarefa.concluida}
          className={`mt-1 flex-shrink-0 ${tarefa.concluida ? 'text-emerald-500' : 'text-slate-300 hover:text-emerald-500'}`}
        >
          {tarefa.concluida ? (
            <CheckCircle2 className="w-6 h-6" />
          ) : (
            <Circle className="w-6 h-6" />
          )}
        </button>

        {/* Conteúdo */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3
                className={`font-bold tracking-tight ${tarefa.concluida ? 'text-slate-400 line-through' : 'text-slate-900'}`}
              >
                {tarefa.tipo && tipoConfig[tarefa.tipo] && (
                  <span className="mr-2">{tipoConfig[tarefa.tipo].icon}</span>
                )}
                {tarefa.titulo}
              </h3>
              {tarefa.descricao && (
                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{tarefa.descricao}</p>
              )}
            </div>

            {/* Prioridade */}
            <span className="flex-shrink-0">
              <Badge tone={prioridadeConfig[tarefa.prioridade]?.tone || 'neutral'}>
                {prioridadeConfig[tarefa.prioridade]?.label || tarefa.prioridade}
              </Badge>
            </span>
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
            <div
              className={`flex items-center gap-1 ${
                isAtrasada ? 'text-rose-600 font-semibold' : isHoje ? 'text-amber-600' : ''
              }`}
            >
              <Calendar className="w-4 h-4" />
              {isAtrasada ? (
                <span>Atrasada - {formatDate(tarefa.dataVencimento)}</span>
              ) : isHoje ? (
                <span>Hoje</span>
              ) : (
                <span>{formatDate(tarefa.dataVencimento)}</span>
              )}
            </div>

            {tarefa.usuarioNome && (
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{tarefa.usuarioNome}</span>
              </div>
            )}

            {tarefa.leadNome && (
              <button
                onClick={() => onNavigate(`/crm/leads/${tarefa.leadId}`)}
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                Lead: {tarefa.leadNome}
              </button>
            )}

            {tarefa.veiculoDescricao && (
              <button
                onClick={() => onNavigate(`/veiculos/${tarefa.veiculoId}`)}
                className="font-semibold text-primary-600 hover:text-primary-700"
              >
                {tarefa.veiculoDescricao}
              </button>
            )}

            {tarefa.concluida && tarefa.dataConclusao && (
              <span className="text-emerald-600">Concluída em {formatDateTime(tarefa.dataConclusao)}</span>
            )}
          </div>
        </div>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={onToggleMenu}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <MoreVertical className="w-5 h-5" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full z-10 mt-1 w-48 rounded-xl border border-slate-200/70 bg-white py-1 shadow-lg">
              <button
                onClick={() => onNavigate(`/crm/tarefas/${tarefa.id}`)}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              {!tarefa.concluida && (
                <button
                  onClick={onConcluir}
                  className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Check className="w-4 h-4" />
                  Concluir
                </button>
              )}
              <button
                onClick={onDelete}
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// ==================== COMPONENTE PRINCIPAL ====================

export function TarefasList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useNotification();

  const [data, setData] = useState<TarefasResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [menuOpen, setMenuOpen] = useState<number | null>(null);

  // Filtros
  const [filtro, setFiltro] = useState({
    concluida: searchParams.get('concluida') || '',
    atrasadas: searchParams.get('atrasadas') === 'true',
    prioridade: searchParams.get('prioridade') || '',
    tipo: searchParams.get('tipo') || '',
    usuarioId: searchParams.get('usuarioId') || '',
    busca: searchParams.get('busca') || '',
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 20,
  });

  // Debounce na busca
  const debouncedBusca = useDebounce(filtro.busca, 300);

  const fetchTarefas = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtro.concluida !== '') params.append('concluida', filtro.concluida);
      if (filtro.atrasadas) params.append('atrasadas', 'true');
      if (filtro.prioridade) params.append('prioridade', filtro.prioridade);
      if (filtro.tipo) params.append('tipo', filtro.tipo);
      if (filtro.usuarioId) params.append('usuarioId', filtro.usuarioId);
      if (debouncedBusca) params.append('busca', debouncedBusca);
      params.append('page', filtro.page.toString());
      params.append('pageSize', filtro.pageSize.toString());

      // Uma única chamada ao invés de 3
      const response = await api.get<TarefasResponse>(`/tarefas/completo?${params}`, { signal });
      setData(response.data);
    } catch (error: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        showError('Erro ao carregar tarefas');
      }
    } finally {
      setIsLoading(false);
    }
  }, [filtro.concluida, filtro.atrasadas, filtro.prioridade, filtro.tipo, filtro.usuarioId, debouncedBusca, filtro.page, filtro.pageSize, showError]);

  useEffect(() => {
    const controller = new AbortController();
    fetchTarefas(controller.signal);
    return () => controller.abort();
  }, [fetchTarefas]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filtro.concluida !== '') params.set('concluida', filtro.concluida);
    if (filtro.atrasadas) params.set('atrasadas', 'true');
    if (filtro.prioridade) params.set('prioridade', filtro.prioridade);
    if (filtro.tipo) params.set('tipo', filtro.tipo);
    if (filtro.usuarioId) params.set('usuarioId', filtro.usuarioId);
    if (filtro.busca) params.set('busca', filtro.busca);
    if (filtro.page > 1) params.set('page', filtro.page.toString());
    setSearchParams(params);
  }, [filtro, setSearchParams]);

  const handleConcluir = useCallback(async (id: number) => {
    try {
      await api.post(`/tarefas/${id}/concluir`);
      showSuccess('Tarefa concluída');
      fetchTarefas();
    } catch {
      showError('Erro ao concluir tarefa');
    }
    setMenuOpen(null);
  }, [fetchTarefas, showSuccess, showError]);

  const handleDelete = useCallback(async (id: number) => {
    if (!confirm('Deseja realmente excluir esta tarefa?')) return;
    try {
      await api.delete(`/tarefas/${id}`);
      showSuccess('Tarefa excluída');
      fetchTarefas();
    } catch {
      showError('Erro ao excluir tarefa');
    }
    setMenuOpen(null);
  }, [fetchTarefas, showSuccess, showError]);

  const isAtrasada = useCallback((tarefa: Tarefa) => {
    return !tarefa.concluida && new Date(tarefa.dataVencimento) < new Date();
  }, []);

  const isHoje = useCallback((dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }, []);

  const totalPages = useMemo(() => Math.ceil((data?.totalItems || 0) / filtro.pageSize), [data?.totalItems, filtro.pageSize]);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const { items: tarefas, totalItems, contadores, vendedores } = data;

  return (
    <div className="space-y-6">
      <PageHeader title="Tarefas" subtitle="Gerencie suas tarefas e follow-ups">
        <button onClick={() => navigate('/crm/tarefas/nova')} className={buttonClass('primary')}>
          <Plus className="w-4 h-4" />
          Nova Tarefa
        </button>
      </PageHeader>

      {/* Contadores */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard
          title="Pendentes"
          value={contadores.pendentes}
          icon={<Circle className="h-[18px] w-[18px]" />}
          onClick={() => setFiltro((f) => ({ ...f, concluida: 'false', atrasadas: false, page: 1 }))}
          delay={0}
        />
        <StatCard
          title="Atrasadas"
          value={contadores.atrasadas}
          icon={<AlertTriangle className="h-[18px] w-[18px]" />}
          tone={contadores.atrasadas > 0 ? 'negative' : 'default'}
          onClick={() => setFiltro((f) => ({ ...f, concluida: '', atrasadas: true, page: 1 }))}
          delay={60}
        />
        <StatCard
          title="Para Hoje"
          value={contadores.paraHoje}
          icon={<Clock className="h-[18px] w-[18px]" />}
          delay={120}
        />
        <StatCard
          title="Concluídas Hoje"
          value={contadores.concluidasHoje}
          icon={<CheckCircle2 className="h-[18px] w-[18px]" />}
          tone="positive"
          delay={180}
        />
      </div>

      {/* Filtros */}
      <Panel title="Filtros" icon={<Filter className="h-4 w-4" />} bodyClassName="p-0">
        <div className="border-b border-slate-100 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <SearchInput
              value={filtro.busca}
              onChange={(value) => setFiltro((f) => ({ ...f, busca: value, page: 1 }))}
              placeholder="Buscar tarefas..."
            />
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
                showFilters ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
            <p className="ml-auto text-sm text-slate-400">{totalItems} tarefa(s)</p>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 md:grid-cols-5">
            <select
              value={filtro.concluida}
              onChange={(e) => setFiltro((f) => ({ ...f, concluida: e.target.value, atrasadas: false, page: 1 }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Todas</option>
              <option value="false">Pendentes</option>
              <option value="true">Concluídas</option>
            </select>

            <select
              value={filtro.prioridade}
              onChange={(e) => setFiltro((f) => ({ ...f, prioridade: e.target.value, page: 1 }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Prioridade</option>
              <option value="URGENTE">Urgente</option>
              <option value="ALTA">Alta</option>
              <option value="MEDIA">Média</option>
              <option value="BAIXA">Baixa</option>
            </select>

            <select
              value={filtro.tipo}
              onChange={(e) => setFiltro((f) => ({ ...f, tipo: e.target.value, page: 1 }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Tipo</option>
              {Object.entries(tipoConfig).map(([key, { label }]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filtro.usuarioId}
              onChange={(e) => setFiltro((f) => ({ ...f, usuarioId: e.target.value, page: 1 }))}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Responsável</option>
              {vendedores.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.nome}
                </option>
              ))}
            </select>

            <button
              onClick={() =>
                setFiltro({
                  concluida: '',
                  atrasadas: false,
                  prioridade: '',
                  tipo: '',
                  usuarioId: '',
                  busca: '',
                  page: 1,
                  pageSize: 20,
                })
              }
              className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100"
            >
              Limpar
            </button>
          </div>
        )}
      </Panel>

      {/* Lista de Tarefas */}
      {tarefas.length === 0 ? (
        <div className="rounded-2xl border border-slate-200/70 bg-white p-8 text-center shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <p className="text-slate-400">Nenhuma tarefa encontrada</p>
          <button
            onClick={() => navigate('/crm/tarefas/nova')}
            className="mt-4 font-semibold text-primary-600 hover:text-primary-700"
          >
            Criar primeira tarefa
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tarefas.map((tarefa) => (
            <TarefaItem
              key={tarefa.id}
              tarefa={tarefa}
              isAtrasada={isAtrasada(tarefa)}
              isHoje={isHoje(tarefa.dataVencimento)}
              menuOpen={menuOpen === tarefa.id}
              onToggleMenu={() => setMenuOpen(menuOpen === tarefa.id ? null : tarefa.id)}
              onConcluir={() => handleConcluir(tarefa.id)}
              onDelete={() => handleDelete(tarefa.id)}
              onNavigate={navigate}
            />
          ))}
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setFiltro((f) => ({ ...f, page: f.page - 1 }))}
            disabled={filtro.page === 1}
          >
            Anterior
          </Button>
          <span className="px-4 py-2 text-sm font-semibold tabular-nums text-slate-600">
            Página {filtro.page} de {totalPages}
          </span>
          <Button
            variant="secondary"
            onClick={() => setFiltro((f) => ({ ...f, page: f.page + 1 }))}
            disabled={filtro.page === totalPages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
